import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AddressFields,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import type { Address } from '@mlm/shared';
import {
  getRegistrationRequirements,
  getShippingZones,
  login,
  registerMerchant,
  uploadFileRequirement,
} from '../lib/api';

const TAB_ORDER = ['account', 'business', 'delivery', 'documents'] as const;
type TabId = (typeof TAB_ORDER)[number];

export function RegisterPage() {
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ['reg-reqs'], queryFn: getRegistrationRequirements });
  const { data: zonesData } = useQuery({ queryKey: ['shipping-zones'], queryFn: getShippingZones });
  const zones = zonesData?.zones ?? [];
  const textReqs = (data?.requirements ?? []).filter((r) => !r.acceptsFile);
  const fileReqs = (data?.requirements ?? []).filter((r) => r.acceptsFile);

  const [tab, setTab] = useState<TabId>('account');
  const [form, setForm] = useState<Record<string, string>>({});
  const [delivery, setDelivery] = useState<Address>({});
  const [reqValues, setReqValues] = useState<Record<string, string>>({});
  const [files, setFiles] = useState<Record<string, File | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // ── Per-tab completeness ───────────────────────────────────────────────
  const accountValid =
    !!form.firstName?.trim() &&
    !!form.lastName?.trim() &&
    !!form.email?.trim() &&
    (form.password?.length ?? 0) >= 8 &&
    !!form.phone?.trim();
  const businessValid =
    !!form.businessName?.trim() && !!form.contactNumber?.trim() && !!form.contactEmail?.trim();
  // Everything is required: full delivery address (incl. line 2), shipping zone,
  // and reachable contacts.
  const deliveryValid =
    !!form.pickupRegionCode &&
    !!delivery.line1?.trim() &&
    !!delivery.line2?.trim() &&
    !!delivery.regionCode &&
    !!delivery.provinceCode &&
    !!delivery.cityCode &&
    !!delivery.barangayCode &&
    !!delivery.postalCode?.trim() &&
    !!delivery.contactNo?.trim() &&
    !!delivery.contactPerson?.trim() &&
    !!delivery.fullAddress?.trim();
  // Every requirement the admin configured is mandatory here.
  const documentsValid =
    textReqs.every((r) => !!reqValues[r.key]?.trim()) && fileReqs.every((r) => !!files[r.key]);

  const tabValid: Record<TabId, boolean> = {
    account: accountValid,
    business: businessValid,
    delivery: deliveryValid,
    documents: documentsValid,
  };
  const allValid = accountValid && businessValid && deliveryValid && documentsValid;

  const idx = TAB_ORDER.indexOf(tab);
  const goNext = () => setTab(TAB_ORDER[Math.min(idx + 1, TAB_ORDER.length - 1)]!);
  const goBack = () => setTab(TAB_ORDER[Math.max(idx - 1, 0)]!);

  async function onSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      await registerMerchant({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        businessName: form.businessName,
        contactNumber: form.contactNumber,
        contactEmail: form.contactEmail,
        pickupRegionCode: form.pickupRegionCode || undefined,
        pickupAddress: delivery,
        requirements: textReqs
          .filter((r) => reqValues[r.key])
          .map((r) => ({ key: r.key, textValue: reqValues[r.key]! })),
      });

      // Upload the staged document files. They need an authenticated session, so
      // sign in with the just-created credentials first. A failed upload never
      // loses the account — the merchant can finish from their Profile page.
      const staged = fileReqs
        .map((r) => ({ key: r.key, file: files[r.key] }))
        .filter((s): s is { key: string; file: File } => !!s.file);
      if (staged.length > 0) {
        try {
          await login({ email: form.email ?? '', password: form.password ?? '' });
          for (const s of staged) {
            try {
              await uploadFileRequirement(s.key, s.file);
            } catch {
              /* leave for the Profile page to retry */
            }
          }
          navigate('/');
          return;
        } catch {
          // Sign-in failed (e.g. 2FA) — fall through to the login redirect; the
          // documents can be uploaded from Profile after signing in.
        }
      }
      navigate('/login?registered=1');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(
        msg === 'email_taken'
          ? 'That email is already registered.'
          : 'Registration failed. Check your details.',
      );
      // Surface the failing area: an email clash belongs to the Account tab.
      if (msg === 'email_taken') setTab('account');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="my-8 w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Become a Merchant</CardTitle>
          <p className="text-sm text-muted-foreground">
            Complete each step. All four are required before you can submit.
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
            <TabsList className="grid w-full grid-cols-4">
              <StepTrigger value="account" n={1} label="Account" done={tabValid.account} />
              <StepTrigger value="business" n={2} label="Business" done={tabValid.business} />
              <StepTrigger value="delivery" n={3} label="Delivery" done={tabValid.delivery} />
              <StepTrigger value="documents" n={4} label="Documents" done={tabValid.documents} />
            </TabsList>

            {/* ── Tab 1: Account (owner) ── */}
            <TabsContent value="account" className="space-y-3 pt-4">
              <p className="text-xs text-muted-foreground">The account owner's login and contact.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required value={form.firstName} onChange={set('firstName')} />
                <Field label="Last name" required value={form.lastName} onChange={set('lastName')} />
                <Field label="Email" type="email" required value={form.email} onChange={set('email')} />
                <Field label="Password" type="password" required value={form.password} onChange={set('password')} />
                <Field label="Contact #" required value={form.phone} onChange={set('phone')} />
              </div>
            </TabsContent>

            {/* ── Tab 2: Business ── */}
            <TabsContent value="business" className="space-y-3 pt-4">
              <p className="text-xs text-muted-foreground">Your store's public business details.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Business name" required value={form.businessName} onChange={set('businessName')} />
                <Field label="Business contact #" required value={form.contactNumber} onChange={set('contactNumber')} />
                <Field label="Business contact email" type="email" required value={form.contactEmail} onChange={set('contactEmail')} />
              </div>
            </TabsContent>

            {/* ── Tab 3: Delivery (shipping origin) ── */}
            <TabsContent value="delivery" className="space-y-3 pt-4">
              <p className="text-xs text-muted-foreground">
                Shipping origin — where couriers pick up your parcels. All fields are required.
              </p>
              <div className="space-y-1">
                <label className="text-sm font-medium">
                  Shipping zone<span className="text-destructive"> *</span>
                </label>
                <Select
                  value={form.pickupRegionCode ?? ''}
                  onValueChange={(v) => setForm((f) => ({ ...f, pickupRegionCode: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="Select your area…" /></SelectTrigger>
                  <SelectContent>
                    {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Determines courier fees on your products.</p>
              </div>
              <AddressFields value={delivery} onChange={setDelivery} requireContactNo showDeliveryExtras requireAll />
            </TabsContent>

            {/* ── Tab 4: Documents (admin-defined requirements) ── */}
            <TabsContent value="documents" className="space-y-4 pt-4">
              {textReqs.length === 0 && fileReqs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No documents are required. You can submit your application.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  The marketplace requires the items below. The admin reviews them with your
                  application before approving your account.
                </p>
              )}

              {textReqs.map((r) => (
                <div key={r.key} className="space-y-1">
                  <label className="text-sm font-medium">
                    {r.label}
                    <span className="text-destructive"> *</span>
                  </label>
                  {r.description && <p className="text-xs text-muted-foreground">{r.description}</p>}
                  <Textarea
                    value={reqValues[r.key] ?? ''}
                    onChange={(e) => setReqValues((v) => ({ ...v, [r.key]: e.target.value }))}
                  />
                </div>
              ))}

              {fileReqs.map((r) => (
                <FileRequirement
                  key={r.key}
                  label={r.label}
                  description={r.description}
                  required
                  accept={r.fileTypes?.join(',')}
                  file={files[r.key]}
                  onPick={(f) => setFiles((prev) => ({ ...prev, [r.key]: f }))}
                />
              ))}
            </TabsContent>
          </Tabs>

          {error && <p className="mt-4 text-sm text-destructive" role="alert">{error}</p>}

          <div className="mt-6 flex items-center gap-2">
            {idx > 0 && (
              <Button type="button" variant="outline" onClick={goBack} disabled={submitting}>
                Back
              </Button>
            )}
            <div className="ml-auto flex items-center gap-2">
              {tab !== 'documents' ? (
                <Button type="button" onClick={goNext} disabled={!tabValid[tab]}>
                  Next
                </Button>
              ) : (
                <Button type="button" onClick={onSubmit} disabled={submitting || !allValid}>
                  {submitting ? 'Submitting…' : 'Register'}
                </Button>
              )}
            </div>
          </div>

          {tab === 'documents' && !allValid && (
            <p className="mt-2 text-right text-xs text-muted-foreground">
              Finish every step (a ✓ on each tab) before submitting.
            </p>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="underline">Sign in</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/** A step tab that shows its number, then a ✓ once the step is complete. */
function StepTrigger({ value, n, label, done }: { value: TabId; n: number; label: string; done: boolean }) {
  return (
    <TabsTrigger value={value} className="flex items-center gap-1.5">
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold leading-none ${
          done ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
        }`}
      >
        {done ? '✓' : n}
      </span>
      <span className="hidden sm:inline">{label}</span>
    </TabsTrigger>
  );
}

/** One file-upload control per admin-defined file requirement (1 file each). */
function FileRequirement({
  label,
  description,
  required,
  accept,
  file,
  onPick,
}: {
  label: string;
  description?: string;
  required: boolean;
  accept?: string;
  file?: File;
  onPick: (f: File | undefined) => void;
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </div>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="mt-2 flex items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5 text-sm hover:bg-muted">
          {file ? 'Change file' : 'Choose file'}
          <input
            type="file"
            accept={accept}
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
          />
        </label>
        {file ? (
          <span className="truncate text-xs text-foreground">{file.name}</span>
        ) : (
          <span className="text-xs text-muted-foreground">No file chosen</span>
        )}
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">
        {props.label}
        {props.required && <span className="text-destructive"> *</span>}
      </label>
      <Input type={props.type ?? 'text'} value={props.value ?? ''} onChange={props.onChange} required={props.required} />
    </div>
  );
}
